import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Avatar,
  Button,
  Card,
  CardContent,

  CircularProgress,
  Chip,
  LinearProgress,
  Stack,
  Tooltip,
  useTheme,
  alpha
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Description as DocumentIcon,
  Notifications as NotificationsIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  PeopleAlt as PeopleIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  CalendarToday as CalendarIcon,
  MoreHoriz as MoreHorizIcon,
  CloudDownload as DownloadIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import Flip from '../../flip.gif';

// Component for analytics card with trend indicator
const StatCard = ({ icon, title, value, change, trend, color }) => {
  const theme = useTheme();


  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: 2,
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)'
        },
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '4px',
          bgcolor: color
        }}
      />
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 500 }}>
              {title}
            </Typography>
            <Typography ariant="h4" component="div" fontWeight="700" sx={{ mb: 1 }}>
              {value?.toLocaleString()}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {trend === 'up' ? (
                <ArrowUpwardIcon fontSize="small" sx={{ color: theme.palette.success.main, mr: 0.5 }} />
              ) : (
                <ArrowDownwardIcon fontSize="small" sx={{ color: theme.palette.error.main, mr: 0.5 }} />
              )}
              <Typography
                variant="body2"
                color={trend === 'up' ? 'success.main' : 'error.main'}
                fontWeight="medium"
              >
                {change}% {trend === 'up' ? 'increase' : 'decrease'}
              </Typography>
            </Box>
          </Box>

          <Avatar
            sx={{
              bgcolor: alpha(color, 0.1),
              color: color,
              width: 48,
              height: 48,
              borderRadius: 2,
              marginTop: "30px"

            }}
          >
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );
};



const AdminDashboard = () => {
  const theme = useTheme();
  const [contractStats, setContractStats] = useState({
    totalContracts: 0,
    pendingContracts: 0,
    paidContracts: 0,
    expiredContracts: 0,
    totalValue: 0
  });

  useEffect(() => {
    const fetchContractStats = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/customer-contracts`);
        const contracts = response.data.data || [];
          const paidContracts = contracts.filter(c => c.payment_status === 'paid');
        const stats = {
          totalContracts: contracts.length,
          pendingContracts: contracts.filter(c => c.payment_status === 'pending').length,
          paidContracts: paidContracts.length,
          expiredContracts: contracts.filter(c => c.status === 'Expired').length,
          totalValue: paidContracts.reduce((sum, c) => sum + (parseFloat(c.grand_total) || 0), 0)
        };
        
        setContractStats(stats);
      } catch (error) {
        console.error('Error fetching contract stats:', error);
      }
    };

    fetchContractStats();
  }, []);
  const [userData, setUserData] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    activeContracts: { value: 0, change: 0, trend: 'up' },
    pendingApproval: { value: 0, change: 0, trend: 'up' },
    expiringSoon: { value: 0, change: 0, trend: 'up' }
  });
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const parsedUserData = JSON.parse(storedUser);
      setUserData(parsedUserData);
    }

    const fetchAllContracts = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/customer-contracts`);
        const data = await response.json();
        setContracts(data.data);
      } catch (error) {
        console.error('Error fetching contracts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllContracts();
  }, []);



  const handleRefresh = () => {
    setLoading(true);
    // In a real app, you would re-fetch the dashboard data here
    setTimeout(() => {
      setLoading(false);
    }, 800);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress size={48} sx={{ mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          Loading Dashboard
        </Typography>
        {/* <img src={ Flip } alt="Loading..." style={{ width: '70px', height: '70px' }} /> */}
      </Box>
    );
  }

  if (!userData) {
    return (
      <Box sx={{ p: 4, textAlign: 'center', maxWidth: 500, mx: 'auto', mt: 8 }}>
        <Typography variant="h5" color="error" gutterBottom>
          Session Expired
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Your user data could not be found. Please log in again to access the dashboard.
        </Typography>
        <Button variant="contained" href="/login" size="large">
          Return to Login
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{
      bgcolor: '#f8fafc',
      minHeight: '100vh',
      pb: 6
    }}>
      <Container maxWidth={false} sx={{ pt: 3 }}>
        {/* Page header */}
        <Box sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', md: 'row' },
          mb: 4
        }}>
          <Box>
            <Typography
              variant="h4"
              component="h1"
              fontWeight="700"
              sx={{ mb: 1 }}
            >
              Dashboard
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Hello {userData?.name || 'User'}, welcome back to your dashboard
            </Typography>
          </Box>
          <Button
            variant="outlined"
            color="inherit"
          
            onClick={handleRefresh}
            disabled={loading}
            sx={{
            
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                borderColor: 'rgba(255, 255, 255, 0.5)',
              }
            }}
          >
            <RefreshIcon sx={{ fontSize: '25px' }} />
          </Button>
{/* 
          <Box sx={{
            display: 'flex',
            gap: 2,
            mt: { xs: 2, md: 0 }
          }}>
            <Button
              variant="outlined"
              startIcon={<SearchIcon />}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 500
              }}
            >
              Search
            </Button>
            <Button
              variant="contained"
              startIcon={<DocumentIcon />}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 500,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
              }}
            >
              New Contract
            </Button>
          </Box> */}
        </Box>

        {/* User profile card */}
        <Card
          elevation={0}
          sx={{
            mb: 4,
            borderRadius: 2,
            backgroundImage: 'linear-gradient(to right, #2196f3, #1976d2)',
            color: 'white',
            boxShadow: '0 10px 20px rgba(37, 99, 235, 0.2)'
          }}
        >
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Grid container spacing={3} alignItems="center">

              <Grid item xs={12} md={8}>
                <Typography variant="h5" component="h2" fontWeight="600" sx={{ mb: 1 }}>
                  {userData.name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                  <Chip
                    label={(userData.role)}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      fontWeight: 500
                    }}
                  />
                 <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                                   <CalendarIcon fontSize="small" sx={{ mr: 0.5 }} />
                                   Last logout: {userData.last_logout ? new Date(userData.last_logout).toLocaleString() : 'N/A'}
                                 </Typography>
                  <Typography variant="body2">
                    {userData.email}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end', position: "absolute" }}>
                 

                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Statistics section */}
        <Typography
          variant="h6"
          component="h2"
          sx={{
            mb: 2,
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <DashboardIcon sx={{ mr: 1, color: 'primary.main' }} />
          Contract Analytics
        </Typography>
        

        {/* Contract Statistics */}
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<AssessmentIcon />}
              title="Total Contracts"
              value={contractStats.totalContracts}
              change={12}
              trend="up"
              color={theme.palette.primary.main}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<AssessmentIcon />}
              title="Pending Contracts"
              value={contractStats.pendingContracts}
              change={5}
              trend="up"
              color={theme.palette.warning.main}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<AssessmentIcon />}
              title="Paid Contracts"
              value={contractStats.paidContracts}
              change={8}
              trend="up"
              color={theme.palette.success.main}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<AssessmentIcon />}
              title="Contract Value"
              value={`$${contractStats.totalValue.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}`}
              change={15}
              trend="up"
              color={theme.palette.info.main}
            />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default AdminDashboard;